// Global Instructions Rule Applied!

import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import {
  fetchFundInvestmentSummaryFiltered,
  fetchFundInvestmentSummaryFilterOptions,
  type FundInvestmentSummaryPaginatedResponse
} from '../services/fundsService.ts'
import { useMenuItemUserPreference } from '../../global_configs/useMenuItemUserPreference.ts'
import { mergeUserPreferenceRowData } from '../../../state_manager/state_update_actions/global_configs/actions.ts'
import {
  areFilterValuesEqual,
  areSortConfigsEqual,
  getSubPreferenceRow,
  PREF_SUBKEY_FUND_INVESTMENT_SUMMARY,
  PREF_TABLE_COLUMN_KEYS,
  PREF_TABLE_FILTERS,
  PREF_TABLE_SORTING,
  resolveTableFiltersFromPreferenceRow,
  resolveTableSortingFromPreferenceRow,
  type FilterValue,
  type SortConfig
} from '../../global_configs/tableViewPreferences.ts'
import { MENU_ITEM_KEY_FUNDS } from '../../../state_manager/globals.ts'
import { fetchGlobalConfigsThunk } from '../../global_configs/controller.ts'
import type { AppDispatch } from '../../../state_manager/store.ts'

const DEFAULT_INVESTMENT_SUMMARY_FILTERS: FilterValue[] = []
const DEFAULT_INVESTMENT_SUMMARY_SORTING: SortConfig[] = [{ key: 'accounts__name', direction: 'asc' }]

/**
 * useFundInvestmentSummary — local-state hook for the per-fund Investment
 * Summary table on the Fund Dashboard. Mirrors `useFundsData` in shape but
 * stores its filters/sorting under a sub-key in the funds menu_item prefs row
 * (so it doesn't clobber the main Funds tablegrid prefs).
 *
 * Data, filterOptions, loading, and the active filters/sorting are local
 * because (a) multiple fund tabs can be open at once, and (b) prefs are the
 * source of cross-session state.
 */
export function useFundInvestmentSummary(fundId: string | null | undefined) {
  const dispatch = useDispatch<AppDispatch>()
  const recordIdString = fundId ? String(fundId) : null

  const { menuItemId, preferenceRow, persistPreference } = useMenuItemUserPreference(
    MENU_ITEM_KEY_FUNDS
  )

  const [data, setData] = useState<Record<string, unknown>[]>([])
  const [filterOptions, setFilterOptions] = useState<Record<string, Array<{ value: string; label: string }>>>({})
  const [activeFilters, setActiveFiltersLocal] = useState<FilterValue[]>(DEFAULT_INVESTMENT_SUMMARY_FILTERS)
  const [activeSorting, setActiveSortingLocal] = useState<SortConfig[]>(DEFAULT_INVESTMENT_SUMMARY_SORTING)
  const [hasMore, setHasMore] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [prefsReady, setPrefsReady] = useState(false)
  const skipPersistRef = useRef(true)

  // 1. Hydrate filters/sorting from prefs (under sub-key).
  useEffect(() => {
    const sub = getSubPreferenceRow(preferenceRow, PREF_SUBKEY_FUND_INVESTMENT_SUMMARY)
    const nextFilters = resolveTableFiltersFromPreferenceRow(sub, DEFAULT_INVESTMENT_SUMMARY_FILTERS)
    const nextSorting = resolveTableSortingFromPreferenceRow(sub, DEFAULT_INVESTMENT_SUMMARY_SORTING)
    if (!areFilterValuesEqual(nextFilters, activeFilters)) setActiveFiltersLocal(nextFilters)
    if (!areSortConfigsEqual(nextSorting, activeSorting)) setActiveSortingLocal(nextSorting)
    setPrefsReady(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferenceRow])

  // 2. Fetch filter options once we have a fund id.
  useEffect(() => {
    if (!recordIdString) {
      setFilterOptions({})
      return
    }
    let cancelled = false
    setFilterOptionsLoading(true)
    ;(async () => {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchFundInvestmentSummaryFilterOptions(recordIdString, token)
      if (!cancelled) {
        setFilterOptions(res.data || {})
        setFilterOptionsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [recordIdString])

  // 3. Fetch data on filter/sort/fundId change (after prefs are ready).
  const fetchPage1 = useCallback(
    async (sorting: SortConfig[], filters: FilterValue[]) => {
      if (!recordIdString) return
      setLoading(true)
      setError(null)
      try {
        const token = getAccessToken(store.getState().auth)
        const res: FundInvestmentSummaryPaginatedResponse = await fetchFundInvestmentSummaryFiltered(
          recordIdString,
          sorting,
          filters,
          1,
          null,
          token
        )
        if (res.error) {
          setError(res.error)
          setData([])
          setHasMore(false)
        } else {
          setData(res.data)
          setHasMore(res.hasMore)
          setCurrentPage(1)
        }
      } finally {
        setLoading(false)
      }
    },
    [recordIdString]
  )

  useEffect(() => {
    if (!prefsReady || !recordIdString) return
    void fetchPage1(activeSorting, activeFilters)
  }, [prefsReady, recordIdString, activeFilters, activeSorting, fetchPage1])

  // 4. Persist filter/sort changes to user prefs (under the sub-key).
  const persistMergedSubPreference = useCallback(
    async (extra: Record<string, unknown> = {}) => {
      if (!menuItemId) return { success: false as const, error: 'No menu item' }
      const reports = store.getState().globalConfigs?.user_preferences_reports ?? []
      const hadExistingRow = reports.some((p: any) => p.menu_item_id === menuItemId)
      const row = reports.find((p: any) => p.menu_item_id === menuItemId)
      const baseData = (row?.data ?? {}) as Record<string, unknown>
      const baseSubData = (baseData[PREF_SUBKEY_FUND_INVESTMENT_SUMMARY] as Record<string, unknown>) || {}
      const newSubData: Record<string, unknown> = {
        ...baseSubData,
        ...extra,
        [PREF_TABLE_FILTERS]: activeFilters,
        [PREF_TABLE_SORTING]: activeSorting
      }
      const merged: Record<string, unknown> = {
        ...baseData,
        [PREF_SUBKEY_FUND_INVESTMENT_SUMMARY]: newSubData
      }
      const result = await persistPreference(undefined, merged)
      if (result.success) {
        if (hadExistingRow) {
          dispatch(mergeUserPreferenceRowData(menuItemId, merged))
        } else {
          const user = store.getState().auth.user
          if (user) {
            await fetchGlobalConfigsThunk(user)(dispatch)
          }
        }
      }
      return result
    },
    [menuItemId, activeFilters, activeSorting, persistPreference, dispatch]
  )

  useEffect(() => {
    if (!prefsReady || !menuItemId) return
    if (skipPersistRef.current) {
      skipPersistRef.current = false
      return
    }
    void persistMergedSubPreference().catch((err) => {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to persist fund investment summary preferences:', err)
      }
    })
  }, [prefsReady, menuItemId, activeFilters, activeSorting, persistMergedSubPreference])

  // 5. Public handlers.
  const handleSetFilters = useCallback(
    (filters: FilterValue[]) => {
      if (areFilterValuesEqual(filters, activeFilters)) return
      setActiveFiltersLocal(filters)
    },
    [activeFilters]
  )

  const handleSetSorting = useCallback(
    (sorting: SortConfig[]) => {
      if (areSortConfigsEqual(sorting, activeSorting)) return
      setActiveSortingLocal(sorting)
    },
    [activeSorting]
  )

  const handleClearFilters = useCallback(() => {
    setActiveFiltersLocal([])
  }, [])

  const handleClearSorting = useCallback(() => {
    setActiveSortingLocal(DEFAULT_INVESTMENT_SUMMARY_SORTING)
  }, [])

  const handleResetFilters = useCallback(() => {
    // Wipe the sub-key from prefs and revert to defaults locally.
    if (!menuItemId) {
      setActiveFiltersLocal(DEFAULT_INVESTMENT_SUMMARY_FILTERS)
      setActiveSortingLocal(DEFAULT_INVESTMENT_SUMMARY_SORTING)
      return
    }
    void (async () => {
      const reports = store.getState().globalConfigs?.user_preferences_reports ?? []
      const row = reports.find((p: any) => p.menu_item_id === menuItemId)
      const baseData = (row?.data ?? {}) as Record<string, unknown>
      const merged = { ...baseData }
      delete (merged as any)[PREF_SUBKEY_FUND_INVESTMENT_SUMMARY]
      const result = await persistPreference(undefined, merged)
      if (result.success) {
        dispatch(mergeUserPreferenceRowData(menuItemId, merged))
        skipPersistRef.current = true
        setActiveFiltersLocal(DEFAULT_INVESTMENT_SUMMARY_FILTERS)
        setActiveSortingLocal(DEFAULT_INVESTMENT_SUMMARY_SORTING)
      }
    })()
  }, [menuItemId, persistPreference, dispatch])

  const persistColumnFieldOrder = useCallback(
    async (fieldNames: string[]) => {
      return persistMergedSubPreference({ [PREF_TABLE_COLUMN_KEYS]: fieldNames })
    },
    [persistMergedSubPreference]
  )

  const resetColumnLayoutToDefault = useCallback(async () => {
    if (!menuItemId) return { success: false as const, error: 'No menu item' }
    const reports = store.getState().globalConfigs?.user_preferences_reports ?? []
    const row = reports.find((p: any) => p.menu_item_id === menuItemId)
    const baseData = (row?.data ?? {}) as Record<string, unknown>
    const baseSubData = ((baseData[PREF_SUBKEY_FUND_INVESTMENT_SUMMARY] as Record<string, unknown>) || {})
    const newSubData = { ...baseSubData }
    delete (newSubData as any)[PREF_TABLE_COLUMN_KEYS]
    const merged: Record<string, unknown> = {
      ...baseData,
      [PREF_SUBKEY_FUND_INVESTMENT_SUMMARY]: {
        ...newSubData,
        [PREF_TABLE_FILTERS]: activeFilters,
        [PREF_TABLE_SORTING]: activeSorting
      }
    }
    const result = await persistPreference(undefined, merged)
    if (result.success) {
      dispatch(mergeUserPreferenceRowData(menuItemId, merged))
    }
    return result
  }, [menuItemId, activeFilters, activeSorting, persistPreference, dispatch])

  const refetch = useCallback(() => fetchPage1(activeSorting, activeFilters), [fetchPage1, activeSorting, activeFilters])

  const subPreferenceRow = getSubPreferenceRow(preferenceRow, PREF_SUBKEY_FUND_INVESTMENT_SUMMARY)

  return {
    data,
    loading,
    error,
    filterOptions,
    filterOptionsLoading,
    activeFilters,
    activeSorting,
    hasMore,
    currentPage,
    preferenceRow: subPreferenceRow,
    handleSetFilters,
    handleSetSorting,
    handleClearFilters,
    handleClearSorting,
    handleResetFilters,
    persistColumnFieldOrder,
    resetColumnLayoutToDefault,
    refetch,
    defaultTableFilters: DEFAULT_INVESTMENT_SUMMARY_FILTERS,
    defaultTableSorting: DEFAULT_INVESTMENT_SUMMARY_SORTING
  }
}
